import {confirmSkip} from '../../_helpers/skip-confirmation';
import {SbModalService} from '@app/_components/unified/sb-modal/sb-modal.service';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {
  CompleteLoginFlow,
  getAuthFlow,
  getError,
  getLoggedInUser,
  IAuthenticationState,
  LoginFlowRequired,
  LoginUser,
  OneClickLogin,
  LogoutUser,
  ResetAuthState,
  TokenLogin
} from '@app/stores';
import {ToastrService} from 'ngx-toastr';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {OrganizationSsoService, SsoDiscoverResult} from '@app/services/organization-sso.service';
import {UserAbilityService} from '@app/services/user-ability.service';
import {AuthenticationService} from '@app/services/authentication.service';
import {OneTimeSecretFile} from '@app/services/one-time-secret-file';
import { LoginNetworkVisualizationComponent } from './login-network-visualization.component';
import { DatePipe } from '@angular/common';

/** Merged after i18n loads so login copy resolves even if deployed JSON is stale. */
const LOGINPAGE_I18N_EXTRA_EN: Record<string, string> = {
  welcome_back: 'Welcome back',
  email_placeholder: 'you@yourevent.com',
  password_placeholder: '••••••••',
  remember_me: 'Remember me',
  account_verified: 'Your account is verified.',
  new_here: 'New here?',
  book_chat: 'Book a 17-min chat →',
  marketing_badge: 'Peer-to-peer marketing for events',
  marketing_headline_1: 'Turn shares',
  marketing_headline_2: 'into sign-ups.',
  marketing_sub: 'Activate your speakers, exhibitors, and attendees to drive measurable registrations.',
  stat_1_value: '31.9',
  stat_1_unit: '%',
  stat_1_desc: 'Average share-to-signup conversion rate',
  stat_2_value: '18',
  stat_2_unit: '%',
  stat_2_desc: 'Additional attendance driven per event',
  stat_3_value: '17',
  stat_3_unit: ' channels',
  stat_3_desc: 'Public and private platforms for sharing',
  marketing_footnote: 'See exactly who drove every registration.',
  logging_in: 'Signing in…',
  captcha_waiting: 'Complete the security check to continue'
};

const LOGINPAGE_I18N_EXTRA_FR: Record<string, string> = {
  welcome_back: 'Bon retour',
  email_placeholder: 'vous@votreevenement.com',
  password_placeholder: '••••••••',
  remember_me: 'Se souvenir de moi',
  account_verified: 'Votre compte est vérifié.',
  new_here: 'Nouveau ici ?',
  book_chat: 'Réserver un appel de 17 min →',
  marketing_badge: 'Marketing pair à pair pour les événements',
  marketing_headline_1: 'Transformez les partages',
  marketing_headline_2: 'en inscriptions.',
  marketing_sub: 'Mobilisez vos conférenciers, exposants et participants pour générer des inscriptions mesurables.',
  stat_1_value: '31,9',
  stat_1_unit: ' %',
  stat_1_desc: 'Taux moyen de conversion partage vers inscription',
  stat_2_value: '18',
  stat_2_unit: ' %',
  stat_2_desc: 'Fréquentation supplémentaire par événement',
  stat_3_value: '17',
  stat_3_unit: ' canaux',
  stat_3_desc: 'Plateformes publiques et privées pour partager',
  marketing_footnote: 'Voyez exactement qui a généré chaque inscription.',
  logging_in: 'Connexion…',
  captcha_waiting: 'Terminez la vérification de sécurité pour continuer'
};

@Component({
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, FormsModule, RouterLink, LoginNetworkVisualizationComponent, DatePipe, TranslateModule],
  host: {
    '(window:pagehide)': 'clearRecoveryCodes()',
  }
})


export class LoginComponent implements OnInit, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toastrService = inject(ToastrService);
  private translate = inject(TranslateService);
  private authenticationStore = inject<Store<IAuthenticationState>>(Store);
  private organizationSsoService = inject(OrganizationSsoService);
  private userAbilityService = inject(UserAbilityService);
  private authenticationService = inject(AuthenticationService);
  private sbModal = inject(SbModalService);

  loginForm: FormGroup<{
    email: FormControl<string | null>;
    remember: FormControl<boolean | null>;
    password: FormControl<string | null>;
  }>;
  submitted = false;
  returnUrl: string;
  authMessage: object;
  token: string;
  unsubscriber = new Subject();
  successTokenUrl: any;
  ssoDiscover: SsoDiscoverResult | null = null;
  ssoDiscoverLoading = false;
  ssoError = '';
  authFlow: any = null;
  /** True when a challenge is required but cannot be drawn (no site key configured). */
  captchaUnavailable = false;
  mfaCode = '';
  recoveryMode = false;
  mfaMethod = 'totp';
  rememberMfaDevice = false;
  emailCodeSent = false;
  totpEnrollment: any = null;
  totpDigits = 6;
  totpConfirming = false;
  mfaEnrollmentError = '';
  emailEnrollment: any = null;
  mfaAvailableMethods: string[] = [];
  recoveryCodesReady = false;
  recoveryCodesAcknowledged = false;
  turnstileSiteKey = '';
  turnstileToken = '';
  // What turnstile.render() handed back, so the widget can be reset by id rather than
  // by guessing which one is on the page.
  turnstileWidgetId: string | null = null;
  rememberDeviceDays = 0;
  // True from the moment the credentials are sent until the store answers - with a
  // user, a flow or an error. The button is disabled meanwhile, so one click is one
  // request; a second submit would spend the CAPTCHA token the first is carrying.
  loginPending = false;
  // True from the moment the store hands back a user until the router swaps this page out
  // for the destination. The user is set before the destination module has even started
  // loading, and the navbar appears the instant it is set, so this page pins itself over
  // everything (`.login-page--handoff`) and the button stays busy until it is destroyed.
  handingOff = false;

  constructor() {
    this.authenticationStore.dispatch(ResetAuthState({params: {error: ''}}));
    this.subscribeStores();
  }

  subscribeStores() {
    // redirect to home if already logged in
    this.authenticationStore.pipe(select(getLoggedInUser))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(user => {
        if (user) {
          this.loginPending = false;
          this.handingOff = true;
          if (user.isPasswordReadyForLogin()) {
            const target = this.userAbilityService.resolvePostLoginUrl(user, this.returnUrl);
            let qp = '';
            if (this.returnUrl?.includes('?')) {
              const returnPath = decodeURI(this.returnUrl.split('?')[0] || '');
              const normalized = !returnPath || returnPath.startsWith('/') ? returnPath : `/${returnPath}`;
              if (normalized === target) {
                qp = this.returnUrl.split('?')[1] || '';
              }
            }
            this.router.navigate([target], {queryParams: this.getQueryParams(qp)});
          } else {
            this.router.navigate(['/resetpassword']);
          }
          this.cdr.markForCheck();
        }
      });

    this.authenticationStore.pipe(select(getError))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(error => {
        if (error) {
          this.loginPending = false;
          this.enrollmentFinishing = false;
          // The attempt spent whatever challenge token it carried, so renew it before
          // the next one rather than re-sending a token the server has already used.
          this.turnstileToken = '';
          setTimeout(() => this.renderTurnstile());
          this.toastrService.error(error);
          this.cdr.markForCheck();
        }
      });

    this.authenticationStore.pipe(select(getAuthFlow))
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(flow => {
        // The flow decides which step the page shows; most of the fields below are bound.
        this.cdr.markForCheck();
        this.authFlow = flow;
        // Any answered flow means the attempt is over and the token it carried is spent.
        if (flow) {
          this.loginPending = false;
          this.enrollmentFinishing = false;
          this.turnstileToken = '';
        }
        if (flow?.code === 'AUTH_MFA_REQUIRED') {
          const availableMethods = (flow.methods || []).filter((method: string) =>
            ['totp', 'email'].includes(method)
          );
          if (!availableMethods.includes(this.mfaMethod)) {
            this.mfaMethod = availableMethods[0] || 'totp';
          }
          this.emailCodeSent = false;
          this.recoveryMode = false;
        }
        if (flow?.code === 'AUTH_CAPTCHA_REQUIRED') {
          setTimeout(() => this.renderTurnstile());
        }
        if (flow?.code === 'AUTH_PASSWORD_RESET_REQUIRED' && flow.reset_token) {
          this.router.navigate(['/resetpassword'], {
            queryParams: {
              token: flow.reset_token,
              reason: flow.reset_reason || undefined,
              // The reset screen offers a one-time week's postponement only when the
              // server says this particular reset allows it.
              deferrable: flow.deferrable ? '1' : undefined,
              deferrals_left: flow.deferrable ? String(flow.deferrals_left ?? '') : undefined,
              deferral_days: flow.deferrable ? String(flow.deferral_days ?? '') : undefined,
              // The reset screen signs in again with the new password, and that sign-in
              // keeps "Remember me" as it was ticked here. The form may not exist yet when a
              // flow left in the store fires on construction.
              remember: this.loginForm?.value?.remember === true ? '1' : undefined
            }
          });
        }
      });
  }

  ngOnInit() {
    const lang = this.translate.currentLang || this.translate.defaultLang || 'en';
    this.translate.use(lang).pipe(take(1)).subscribe(() => {
      this.translate.setTranslation('en', { Loginpage: LOGINPAGE_I18N_EXTRA_EN }, true);
      this.translate.setTranslation('fr', { Loginpage: LOGINPAGE_I18N_EXTRA_FR }, true);
    });

    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      remember: [false],
      // No length rule here: the login form must accept whatever a legacy account still
      // has, or the people the server wants to send to the forced-reset screen never
      // reach it. The policy is enforced server-side at login.
      password: ['', [Validators.required]]
    });
    this.authenticationService.getPublicPolicy()
      .pipe(takeUntil(this.unsubscriber))
      .subscribe((response: any) => {
        this.turnstileSiteKey = response?.data?.login?.turnstileSiteKey || '';
        this.mfaAvailableMethods = response?.data?.mfa?.methods || ['totp'];
        this.rememberDeviceDays = Number(response?.data?.mfa?.rememberDeviceDays || 0);
        if (this.authFlow?.code === 'AUTH_CAPTCHA_REQUIRED') {
          setTimeout(() => this.renderTurnstile());
        }
        this.cdr.markForCheck();
      });

    // Prefer returnUrl query; empty/`/` resolve to role default home after login.
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '';
    const fragmentParams = new URLSearchParams(this.route.snapshot.fragment || '');
    const oneClickToken = fragmentParams.get('one_click_token')
      || this.route.snapshot.queryParams['one_click_token'];
    if (oneClickToken) {
      const queryParams = {...this.route.snapshot.queryParams};
      delete queryParams['one_click_token'];
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        fragment: null,
        replaceUrl: true
      });
      this.authenticationStore.dispatch(OneClickLogin({token: oneClickToken}));
    }
    // The one-click link from the sign-in code email: the challenge and the link token
    // travel in the fragment, like the one-click login link. Completed straight away rather
    // than shown as a code form - if the link is spent or stale, the server's message says
    // so and the plain sign-in form is the right place to land.
    const linkChallenge = fragmentParams.get('mfa_challenge');
    const linkToken = fragmentParams.get('mfa_link');
    if (linkChallenge && linkToken) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {...this.route.snapshot.queryParams},
        fragment: null,
        replaceUrl: true
      });
      this.authenticationStore.dispatch(CompleteLoginFlow({
        flow: {code: 'AUTH_MFA_REQUIRED', challenge_token: linkChallenge},
        method: 'email',
        linkToken
      }));
    }
    this.token = this.route.snapshot.queryParams['token'];
    if (this.token) {
      this.tokenLogin();
    }
    if (this.route.snapshot.queryParams['sso'] === 'success') {
      this.authenticationStore.dispatch(TokenLogin({token: ''}));
    }
    if (this.route.snapshot.queryParams['mfa_enrollment'] === 'required') {
      this.authenticationService.refreshSession().subscribe({
        next: (response: any) => {
          this.authenticationService.setAccessToken(response.token);
          this.authenticationStore.dispatch(LoginFlowRequired({
            flow: {code: 'AUTH_MFA_ENROLLMENT_REQUIRED', token: response.token}
          }));
        },
        error: () => this.toastrService.error('Unable to continue MFA enrollment')
      });
    }
    const mfaChallenge = this.route.snapshot.queryParams['mfa_challenge'];
    if (mfaChallenge) {
      this.authenticationStore.dispatch(LoginFlowRequired({
        flow: {code: 'AUTH_MFA_REQUIRED', challenge_token: mfaChallenge}
      }));
    }
    const sessionConflict = this.route.snapshot.queryParams['session_conflict_token'];
    if (sessionConflict) {
      this.authenticationStore.dispatch(LoginFlowRequired({
        flow: {
          code: 'AUTH_SESSION_CONFLICT',
          conflict_token: sessionConflict,
          maximum_active_sessions: Number(
            this.route.snapshot.queryParams['maximum_active_sessions'] || 1
          )
        }
      }));
    }
    this.successTokenUrl = this.route.snapshot.queryParams['status'];
    if(this.successTokenUrl) {

      this.router.navigate(
        [], {
          relativeTo: this.route,
          queryParams: { status: null },
          queryParamsHandling: 'merge',
        });

    }
    const ssoErrorParam = this.route.snapshot.queryParams['sso_error'];
    if (ssoErrorParam) {
      this.ssoError = decodeURIComponent(ssoErrorParam);
      this.router.navigate(
        [], {
          relativeTo: this.route,
          queryParams: { sso_error: null },
          queryParamsHandling: 'merge',
        });
    }
    this.authMessage = {
      Success: false,
      msg: ''
    };
    // Straight after a password change the person is signed in with the password they just
    // set, through the ordinary submit - so MFA, the session limit and CAPTCHA all still apply.
    const pendingSignIn = this.authenticationService.takePendingSignIn();
    if (pendingSignIn) {
      this.loginForm.patchValue(pendingSignIn);
      this.onSubmit();
    }
  }

  onEmailBlur(): void {
    const email = (this.f.email.value || '').trim().toLowerCase();
    if (!email || this.f.email.errors) {
      this.ssoDiscover = null;
      return;
    }
    this.ssoDiscoverLoading = true;
    this.organizationSsoService.discover(email)
      .pipe(takeUntil(this.unsubscriber))
      .subscribe({
        next: (resp) => {
          this.ssoDiscoverLoading = false;
          this.ssoDiscover = resp.data;
          this.cdr.markForCheck();
        },
        error: () => {
          this.ssoDiscoverLoading = false;
          this.ssoDiscover = null;
          this.cdr.markForCheck();
        }
      });
  }

  continueWithSso(): void {
    if (!this.ssoDiscover?.ssoEnabled || !this.ssoDiscover.organizationId) {
      return;
    }
    this.redirectTo(this.organizationSsoService.getLoginUrl(
      this.ssoDiscover.organizationId,
      this.f.remember.value === true
    ));
  }

  /** Leave the app for the identity provider; a method of its own so a spec can stand in. */
  redirectTo(url: string): void {
    window.location.href = url;
  }

  tokenLogin() {
    this.authenticationStore.dispatch(LogoutUser());
    this.authenticationStore.dispatch(TokenLogin({token: this.token}));
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  /**
   * Returns Auth token object that will use for next navigation.
   *
   * @param  url  an absolute URL giving the base location of the Application.
   * @param  name the location of the request, relative to the url.
   * @return      Auth token object with user data.
   */

  /**
   * The challenge has been asked for but not yet answered: the script is still
   * loading, the widget is drawing, or the visitor has not solved it. Submitting now
   * would only earn another AUTH_CAPTCHA_REQUIRED, so the button waits.
   */
  get captchaPending(): boolean {
    return this.authFlow?.code === 'AUTH_CAPTCHA_REQUIRED' && !this.turnstileToken;
  }

  get loginSubmitDisabled(): boolean {
    return this.loginPending || this.captchaPending || this.handingOff;
  }

  onSubmit() {
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid || this.loginSubmitDisabled) {
      return;
    }
    this.loginPending = true;
    this.authenticationStore.dispatch(LoginUser({
      credentials: {
        email: this.f.email.value,
        password: this.f.password.value,
        remember: this.f.remember.value,
        turnstileToken: this.turnstileToken
      }
    }));
  }

  /**
   * Authenticator apps and the code email show the digits grouped ("123 456"), and a
   * paste brings the grouping along. The server compares the characters only, so
   * whitespace is dropped as the code is typed and once more on submit, for a trailing
   * space the field may still be showing.
   */
  static normalizeMfaCode(value: unknown): string {
    return String(value ?? '').replace(/\s+/g, '');
  }

  onMfaCodeInput(value: string): void {
    this.mfaCode = LoginComponent.normalizeMfaCode(value);
  }

  completeMfa(): void {
    const code = LoginComponent.normalizeMfaCode(this.mfaCode);
    if (!code) {
      return;
    }
    this.mfaCode = code;
    this.authenticationStore.dispatch(CompleteLoginFlow({
      flow: this.authFlow,
      code,
      recovery: this.recoveryMode,
      method: this.recoveryMode ? 'totp' : this.mfaMethod,
      rememberDevice: this.rememberMfaDevice
    }));
  }

  sendEmailMfaCode(): void {
    this.authenticationService.sendMfaEmail(this.authFlow.challenge_token).subscribe({
      next: () => {
        this.emailCodeSent = true;
        this.toastrService.success('Verification code sent');
        this.cdr.markForCheck();
      },
      error: (error) => this.toastrService.error(
        error?.error?.message || 'Unable to send verification code'
      )
    });
  }

  replaceSession(): void {
    this.authenticationStore.dispatch(CompleteLoginFlow({flow: this.authFlow}));
  }

  startTotpEnrollment(): void {
    this.authenticationService.enrollTotp().subscribe({
      next: (response: any) => {
        this.totpEnrollment = response.data;
        this.totpDigits = Number(response?.data?.digits) || 6;
        this.mfaCode = '';
        this.mfaEnrollmentError = '';
        this.cdr.markForCheck();
      },
      error: (error) => this.toastrService.error(error?.error?.message || 'MFA enrollment failed')
    });
  }

  startEmailEnrollment(): void {
    this.authenticationService.enrollEmailMfa().subscribe({
      next: (response: any) => {
        this.emailEnrollment = response.data;
        this.cdr.markForCheck();
      },
      error: (error) => this.toastrService.error(
        error?.error?.message || 'Unable to send email verification code'
      )
    });
  }

  enrollmentSkipping = false;
  // True from an agreed postponement until the store answers with a user or an error.
  enrollmentFinishing = false;

  /**
   * Postpone enrolment from the screen that blocks on it.
   *
   * This is the only place the postponement can be taken from a fresh sign-in: the in-app
   * reminder popup lives behind the very gate this screen is. The token in hand is the
   * enrolment one, which the server admits to this one endpoint for exactly this reason;
   * once the postponement is recorded, the same refresh that follows a completed
   * enrolment yields a full session, because enforcement is no longer in force for the
   * week that was just taken.
   */
  async skipEnrollment(): Promise<void> {
    if (this.enrollmentSkipping || !this.authFlow?.mfa_reminder_deferrable) {
      return;
    }
    this.enrollmentSkipping = true;
    const agreed = await confirmSkip(this.sbModal, this.translate, 'mfa', {
      days: this.authFlow.mfa_reminder_deferral_days,
      skipsLeft: this.authFlow.mfa_reminder_deferrals_left
    });
    this.cdr.markForCheck();
    if (!agreed) {
      this.enrollmentSkipping = false;
      return;
    }
    // The postponement and the refresh behind it take a few seconds. Show the sign-in
    // happening from the moment it was agreed, not the choice that was just made.
    this.enrollmentFinishing = true;
    this.authenticationService.deferMfaReminder().subscribe({
      next: () => {
        this.enrollmentSkipping = false;
        this.toastrService.info(this.translate.instant('AuthSecurity.mfa_nudge_skipped'));
        this.authenticationStore.dispatch(CompleteLoginFlow({
          flow: {...this.authFlow, code: 'AUTH_REFRESH_AFTER_MFA'}
        }));
      },
      error: (error) => {
        this.enrollmentSkipping = false;
        this.enrollmentFinishing = false;
        this.cdr.markForCheck();
        this.toastrService.error(
          error?.error?.message || this.translate.instant('AuthSecurity.mfa_nudge_skip_failed')
        );
      }
    });
  }

  confirmEmailEnrollment(): void {
    const code = LoginComponent.normalizeMfaCode(this.mfaCode);
    if (!this.emailEnrollment?.method_id || !code) {
      return;
    }
    this.mfaCode = code;
    this.authenticationService.confirmEmailMfa(
      this.emailEnrollment.method_id,
      code
    ).subscribe({
      next: () => this.authenticationStore.dispatch(CompleteLoginFlow({
        flow: {...this.authFlow, code: 'AUTH_REFRESH_AFTER_MFA'}
      })),
      error: (error) => this.toastrService.error(error?.error?.message || 'Invalid code')
    });
  }

  confirmTotp(): void {
    if (!this.totpEnrollment?.method_id || this.totpConfirming) {
      return;
    }
    const code = LoginComponent.normalizeMfaCode(this.mfaCode);
    if (!new RegExp(`^\\d{${this.totpDigits}}$`).test(code)) {
      this.mfaEnrollmentError = this.translate.instant('AuthSecurity.totp_code_format', {
        count: this.totpDigits
      });
      return;
    }
    this.mfaCode = code;
    this.mfaEnrollmentError = '';
    this.totpConfirming = true;
    this.authenticationService.confirmTotp(this.totpEnrollment.method_id, code)
      .subscribe({
        next: (response: any) => {
          this.totpConfirming = false;
          this.prepareRecoveryCodes(response.recovery_codes);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.totpConfirming = false;
          this.cdr.markForCheck();
          const response = error?.error;
          if (response?.enrollment_invalidated) {
            this.totpEnrollment = null;
            this.mfaCode = '';
          }
          const message = response?.enrollment_invalidated
            ? this.translate.instant('AuthSecurity.totp_enrollment_attempts_exhausted')
            : Number.isInteger(response?.attempts_remaining)
              ? this.translate.instant('AuthSecurity.totp_attempts_remaining', {
                count: response.attempts_remaining
              })
              : response?.message || 'Invalid code';
          this.mfaEnrollmentError = message;
          this.toastrService.error(message);
        }
      });
  }

  downloadRecoveryCodes(): void {
    this.recoveryCodeFile?.download();
  }

  finishEnrollment(): void {
    if (!this.recoveryCodesAcknowledged) {
      return;
    }
    this.clearRecoveryCodes();
    this.authenticationStore.dispatch(CompleteLoginFlow({
      flow: {...this.authFlow, code: 'AUTH_REFRESH_AFTER_MFA'}
    }));
  }

  private renderTurnstile(): void {
    // Without a site key there is no challenge to draw. Say so, rather than leaving an
    // empty box and an apparently dead form.
    // Called from a timeout: the notice and the button are bound.
    this.cdr.markForCheck();
    if (!this.turnstileSiteKey) {
      this.captchaUnavailable = true;
      return;
    }
    this.captchaUnavailable = false;
    const render = () => {
      const target = document.getElementById('login-turnstile');
      const turnstile = (window as any).turnstile;
      if (!target || !turnstile) {
        return;
      }
      // A solved token is spent by the server on first use, so the challenge has to be
      // renewed after every attempt or the form never gets past it. Two cases:
      //
      // The container still holds a live challenge - ask it for a new token in place.
      if (target.querySelector('iframe')) {
        this.resetTurnstile();
        return;
      }
      // Or it does not, because the alert block this container sits in is re-created
      // whenever the login flow changes. That throws away the element the challenge was
      // drawn into while Turnstile goes on holding an id for it, so resetting by that id
      // only logs "Cannot find Widget". Drop the orphan and draw a new one.
      if (this.turnstileWidgetId && turnstile.remove) {
        try {
          turnstile.remove(this.turnstileWidgetId);
        } catch (_) {
          // Already gone; nothing to release.
        }
      }
      this.turnstileWidgetId = null;
      this.turnstileToken = '';
      target.innerHTML = '';
      this.turnstileWidgetId = turnstile.render(target, {
        sitekey: this.turnstileSiteKey,
        callback: (token: string) => {
          this.turnstileToken = token;
          this.cdr.markForCheck();
        },
        'expired-callback': () => {
          this.turnstileToken = '';
          this.cdr.markForCheck();
        }
      });
    };
    if ((window as any).turnstile) {
      render();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = render;
    document.head.appendChild(script);
  }

  /**
   * Discard the token we are holding and ask the widget for another.
   *
   * A Turnstile token is single-use server-side, so it must be renewed after every
   * attempt it was spent on. Without this the widget keeps showing "Success!" while
   * every further sign-in is refused as a replayed challenge, which strands the user on
   * the login page and - because the refusal is answered before the password is ever
   * checked - means the failure never counts towards the account lock either.
   */
  private resetTurnstile(): void {
    this.turnstileToken = '';
    const turnstile = (window as any).turnstile;
    if (!turnstile?.reset || !this.turnstileWidgetId) {
      return;
    }
    try {
      turnstile.reset(this.turnstileWidgetId);
    } catch (_) {
      // A widget that was never drawn, or whose element has since been removed, has
      // nothing to renew; renderTurnstile draws a replacement.
    }
  }

  getQueryParams(qs = '') {
    qs = qs ? qs.split('+').join(' ') : qs;
    const params = {};
    let tokens;
    const re = /[?&]?([^=]+)=([^&]*)/g;

    while ((tokens = re.exec(qs)) !== null) {
      params[decodeURIComponent(tokens[1])] = decodeURIComponent(tokens[2]);
    }

    return params;
  }

  ngOnDestroy() {
    this.clearRecoveryCodes();
    this.unsubscriber.next(undefined);
    this.unsubscriber.complete();
  }

  clearRecoveryCodes(): void {
    this.recoveryCodeFile?.destroy();
    this.recoveryCodeFile = null;
    this.recoveryCodesReady = false;
    this.recoveryCodesAcknowledged = false;
  }

  private recoveryCodeFile: OneTimeSecretFile | null = null;

  private prepareRecoveryCodes(codes: string[]): void {
    this.clearRecoveryCodes();
    const values = Array.isArray(codes) ? codes.filter(Boolean) : [];
    if (!values.length) {
      return;
    }
    this.recoveryCodeFile = new OneTimeSecretFile(
      values.join('\n'),
      'snoball-recovery-codes.txt'
    );
    this.recoveryCodesReady = this.recoveryCodeFile.available;
    if (Array.isArray(codes)) {
      codes.fill('');
    }
  }
}
